import { PointColorType } from '@pnext/three-loader';

import type { tLayerSettings } from '@/atoms/layers.atom';

import { CLEARANCE_VIOLATION_CLASS } from './types';

/**
 * How the derived clearance cloud answers "which points violated?".
 *
 * Defined ONCE, here (ruling P4): `clearance.atom.ts` imports this type rather
 * than restating the union, so the atom and the patches can never drift.
 */
export type ClearanceViewMode = 'scalar' | 'highlight' | 'only';

/** Render order in the dock's segmented control; `scalar` is the run default. */
export const CLEARANCE_VIEW_MODES = ['scalar', 'highlight', 'only'] as const;

/** Hot magenta: the one hue nothing else in this app's point clouds uses, so a
 *  violating point cannot be confused with a classification, an intensity or a
 *  deviation. */
export const VIOLATION_INK = '#ff2d95';

/** What everything else fades to under `highlight`. Light enough to keep the
 *  structure readable — the violation has to be seen IN CONTEXT, which is
 *  exactly what "hide everything else" throws away. */
export const CONTEXT_INK = '#6b6b66';

/**
 * `PointColorType.CLASSIFICATION`, taken from the loader rather than written
 * out as a literal: `pointCloudMaterialColorSource` is a bare `Int` in the
 * schema, so the wrong number type-checks and renders a plausible-looking
 * cloud. It is 8; an earlier draft of this feature had it as 2, which is DEPTH.
 */
export const CLASSIFICATION_COLOR_SOURCE: number = PointColorType.CLASSIFICATION;

/** ASPRS classification is a single byte; Potree's filter array is sized to match. */
const CLASS_COUNT = 256;

/**
 * What the layer looked like before the dock took its colouring over.
 *
 * Two fields rather than one because `layerSettingsAtom` is a nullable
 * OVERRIDE (`local || remote || default`), not the settings themselves:
 *
 *  - `base` is the RESOLVED settings the mode patches are derived from — never
 *    null, never one of our own taken-over intermediates.
 *  - `restore` is the exact value to write back, which is `null` when the user
 *    had no local override at all. Writing a resolved copy back instead would
 *    silently pin today's remote settings onto the layer forever.
 */
export interface ClearanceViewHold {
  base: tLayerSettings;
  restore: tLayerSettings | null;
}

/**
 * The whole render-mode state for one layer: the mode, and what it owes the
 * user back. Kept together (and persisted together) on purpose — a mode
 * without its snapshot is a take-over that can never be undone.
 */
export interface ClearanceViewState {
  mode: ClearanceViewMode;
  /** null whenever the dock holds nothing — the only state it may be left in. */
  hold: ClearanceViewHold | null;
}

export const CLEARANCE_VIEW_STATE_DEFAULT: ClearanceViewState = { mode: 'scalar', hold: null };

export interface ClearanceViewTransition {
  state: ClearanceViewState;
  /**
   * `null` — leave the layer's settings alone; nothing is owed.
   * `{ settings }` — write exactly this to `layerSettingsAtom`, where `null`
   * means "clear the local override".
   */
  write: { settings: tLayerSettings | null } | null;
}

/**
 * The layer-settings patch a view mode implies.
 *
 *  - `scalar`    — the run's default: CUSTOM_SCALAR over the clearance ramp.
 *                  Returned identically, so "no take-over" is representable.
 *  - `highlight` — CLASSIFICATION colouring, class 13 hot, every other class a
 *                  single muted neutral. Margin gradation is traded away for an
 *                  unmistakable answer to "which points".
 *  - `only`      — `highlight` PLUS the class filter, so nothing but the
 *                  violating points draws. The filter runs BEFORE the colour
 *                  branch in the shader, so it composes with any colour source.
 *
 * Class 13 is force-enabled in both non-scalar modes: a reader who had ground
 * or noise filtered out keeps that filter under `highlight`, but a mode whose
 * entire purpose is to show the violations must not honour a filter that hides
 * them. Pure, so the three modes can be asserted without a WebGL context.
 */
export const viewModeSettings = (
  base: tLayerSettings,
  mode: ClearanceViewMode,
  hasClassification?: boolean,
): tLayerSettings => {
  if (mode === 'scalar') return base;
  // The cloud has been LOADED and demonstrably carries no classification
  // attribute: every point reads class 0, so `highlight` would paint the whole
  // cloud context-grey and `only` would filter it down to nothing. Hand back
  // the untouched base — a scalar-coloured cloud plus a disabled control with
  // a tooltip beats a blank viewport with no explanation. `undefined` means
  // "not loaded yet, don't know", and keeps the pre-existing behaviour.
  if (hasClassification === false) return base;
  const source = base.pointCloudClasses ?? [];
  const classes = Array.from({ length: Math.max(CLASS_COUNT, source.length) }, (_, i) => {
    const violating = i === CLEARANCE_VIOLATION_CLASS;
    return {
      color: violating ? VIOLATION_INK : CONTEXT_INK,
      isEnabled: violating ? true : mode === 'only' ? false : (source[i]?.isEnabled ?? true),
    };
  });
  // `only` FILTERS but does not repaint. The class filter runs in the vertex
  // shader BEFORE the colour branch (see `Pointcloud.tsx`), so hiding every
  // class but 13 composes with whatever colouring the layer already has —
  // and what the reader wants once the clutter is gone is the run's own
  // Clearance ramp, which says HOW DEEP each surviving point is. Repainting
  // them one flat ink threw that away and answered a question they had
  // already answered by choosing this mode: "which points are violations".
  if (mode === 'only') return { ...base, pointCloudClasses: classes };
  // `highlight` is the opposite trade: everything stays on screen, so the
  // violations have to be found by colour, and that needs the classification
  // source with its two inks.
  return { ...base, pointCloudMaterialColorSource: CLASSIFICATION_COLOR_SOURCE, pointCloudClasses: classes };
};

/**
 * Move to `next`, snapshotting the user's settings the first time and NEVER
 * again — `highlight → only` re-derives from the original, so our own magenta
 * can never become the thing we restore to.
 *
 * `current.local` is the raw override atom value (nullable); `current.effective`
 * is `local || remote || default`, the settings actually on screen.
 */
export const takeOverViewMode = (
  state: ClearanceViewState,
  current: { local: tLayerSettings | null; effective: tLayerSettings },
  next: ClearanceViewMode,
  hasClassification?: boolean,
): ClearanceViewTransition => {
  if (next === 'scalar') return releaseViewMode(state);
  // Same guard as `viewModeSettings`, one level up: a mode that cannot render
  // must not be ENTERED either. Taking the release branch (rather than just
  // declining to write) is what makes a mode persisted from before the octree
  // was known to lack classification undo itself on the next mount — the
  // stored state goes back to `scalar` and any hold we owed is paid back,
  // instead of leaving a magenta/blank take-over the UI no longer offers a way
  // to exit.
  if (hasClassification === false) return releaseViewMode(state);
  const hold = state.hold ?? { base: current.effective, restore: current.local };
  return { state: { mode: next, hold }, write: { settings: viewModeSettings(hold.base, next) } };
};

/**
 * Give the layer back. Called on mode → Scalar, on dock close, on unmount and
 * on the dock's next mount after a reload left a take-over persisted — hence
 * "restore exactly once, and only if we were the ones who changed it": with no
 * hold it owes no write at all, so it can be called as often as the lifecycle
 * needs without ever stamping on settings it did not take.
 */
export const releaseViewMode = (state: ClearanceViewState): ClearanceViewTransition =>
  state.hold
    ? { state: CLEARANCE_VIEW_STATE_DEFAULT, write: { settings: state.hold.restore } }
    : { state: CLEARANCE_VIEW_STATE_DEFAULT, write: null };

/**
 * Why the non-scalar render modes cannot be offered right now — or `null` when
 * they can.
 *
 * The distinction the whole seam turns on: `undefined` from
 * `usePointcloudHasClassification` means the cloud has not finished loading
 * (transient, say so and wait), while `false` means the octree was converted
 * WITHOUT the classification attribute (permanent for this cloud — only
 * re-running the analysis fixes it). Collapsing the two into one "disabled"
 * state would tell a user to re-run a job they only had to wait for.
 *
 * Kept here rather than in the dock so the guard is testable without a DOM,
 * and so the dock and the apply path can never disagree about what "usable"
 * means.
 */
export type ViewModeUnavailableReason = 'loading' | 'noClassification';

export const viewModeUnavailableReason = (hasClassification: boolean | undefined): ViewModeUnavailableReason | null => {
  if (hasClassification === undefined) return 'loading';
  return hasClassification ? null : 'noClassification';
};
